import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet-draw'; // ensure editing mixins are available
import { formatFeet } from '../utils/pixelMeasurements';

/**
 * Renders placed items as Leaflet layers on the CRS.Simple map.
 * Each placement gets its own layer, keyed by `${itemId}::${placementId}`.
 * Only placements matching `activePage` are rendered.
 *
 * In L.CRS.Simple: lat = pixelY, lng = pixelX
 */
export default function usePlanRendering({
  mapRef, drawnItemsRef, items, activeItemId, activePage,
  onPlacementClick, pixelsPerFoot = 0, showMeasurements = false,
  editingPlacement = null,
}) {
  const layerMapRef = useRef({});
  const labelLayerRef = useRef(null);

  // Sync placements to map layers — filtered by activePage
  useEffect(() => {
    const map = mapRef.current;
    const drawnItems = drawnItemsRef.current;
    if (!map || !drawnItems) return;

    // When editingPlacement changes, clear all layers to force re-render with correct dimming
    Object.keys(layerMapRef.current).forEach(key => {
      drawnItems.removeLayer(layerMapRef.current[key]);
      delete layerMapRef.current[key];
    });

    const activePlacements = new Set();

    items.forEach(item => {
      const placements = item.placements || [];
      placements
        .filter(p => p.page === activePage)
        .forEach(placement => {
          const key = `${item.itemId}::${placement.id}`;
          activePlacements.add(key);

          const latlngs = (placement.pixelPath || []).map(pt => [pt.y, pt.x]);
          if (!latlngs.length) return;

          let layer;
          const color = item.color || '#666';
          const isEditing = editingPlacement &&
            editingPlacement.itemId === item.itemId &&
            editingPlacement.placementId === placement.id;
          const isDimmed = editingPlacement && !isEditing;

          if (item.geometryType === 'point') {
            const pt = latlngs[0];
            layer = L.marker(pt, {
              icon: L.divIcon({
                className: '',
                html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.4);opacity:${isDimmed ? 0.15 : 1}"></div>`,
                iconSize: [14, 14],
                iconAnchor: [7, 7],
              }),
              interactive: !isDimmed,
            });
          } else if (item.geometryType === 'polyline') {
            layer = L.polyline(latlngs, {
              color, weight: 3, opacity: isDimmed ? 0.15 : 0.8,
              interactive: !isDimmed,
            });
          } else {
            layer = L.polygon(latlngs, {
              color, fillColor: color, fillOpacity: isDimmed ? 0.05 : 0.25,
              weight: 2, opacity: isDimmed ? 0.15 : 0.8,
              interactive: !isDimmed,
            });
          }

          layer._placementMeta = { itemId: item.itemId, placementId: placement.id };
          layer.on('click', () => {
            if (onPlacementClick) onPlacementClick(item.itemId, placement.id);
          });

          drawnItems.addLayer(layer);
          layerMapRef.current[key] = layer;
        });
    });

    // Remove layers no longer active on this page
    Object.keys(layerMapRef.current).forEach(key => {
      if (!activePlacements.has(key)) {
        drawnItems.removeLayer(layerMapRef.current[key]);
        delete layerMapRef.current[key];
      }
    });
  }, [items, activePage, editingPlacement, mapRef, drawnItemsRef, onPlacementClick]);

  // Highlight active item's placements
  useEffect(() => {
    Object.entries(layerMapRef.current).forEach(([key, layer]) => {
      const meta = layer._placementMeta;
      if (!meta) return;
      const isActive = meta.itemId === activeItemId;
      if (layer.setStyle) {
        if (isActive) {
          layer.setStyle({ weight: 4, color: '#1976d2', fillOpacity: 0.4 });
        } else {
          const item = items.find(i => i.itemId === meta.itemId);
          const color = item?.color || '#666';
          if (item?.geometryType === 'polyline') {
            layer.setStyle({ weight: 3, color, opacity: 0.8 });
          } else {
            layer.setStyle({ weight: 2, color, fillColor: color, fillOpacity: 0.25 });
          }
        }
      }
    });
  }, [activeItemId, items]);

  // Render measurement labels
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (labelLayerRef.current) {
      map.removeLayer(labelLayerRef.current);
      labelLayerRef.current = null;
    }
    if (!showMeasurements || !pixelsPerFoot) return;

    const labelGroup = L.layerGroup();

    items.forEach(item => {
      if (item.geometryType === 'point') return;
      (item.placements || [])
        .filter(p => p.page === activePage)
        .forEach(placement => {
          const pts = placement.pixelPath || [];
          if (pts.length < 2) return;
          const isClosed = item.geometryType === 'polygon';
          const edges = isClosed ? pts.length : pts.length - 1;
          for (let i = 0; i < edges; i++) {
            const a = pts[i];
            const b = pts[(i + 1) % pts.length];
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const distPx = Math.sqrt(dx * dx + dy * dy);
            const distFt = distPx / pixelsPerFoot;
            if (distFt < 0.5) continue;
            const midY = (a.y + b.y) / 2;
            const midX = (a.x + b.x) / 2;
            const label = formatFeet(distFt);
            const marker = L.marker([midY, midX], {
              icon: L.divIcon({
                className: 'measurement-label',
                html: `<span style="background:rgba(255,255,255,0.85);padding:1px 4px;font-size:10px;border-radius:3px;white-space:nowrap">${label}</span>`,
                iconAnchor: [20, 8],
              }),
              interactive: false,
            });
            labelGroup.addLayer(marker);
          }
        });
    });

    labelGroup.addTo(map);
    labelLayerRef.current = labelGroup;
  }, [items, activePage, showMeasurements, pixelsPerFoot, mapRef]);

  return { layerMapRef };
}
